import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const PostScoreForm = () => {
  const getLocalDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const location = useLocation();
  const smartData = location.state?.smartData || null;

  const [formData, setFormData] = useState({
    playerName: '',
    tableName: smartData?.tableName || '',
    tableType: smartData?.tableType || '',
    score: smartData?.scores?.length > 0
      ? String(smartData.scores.reduce((max, s) => Math.max(max, s.value), 0))
      : '',
    datePlayed: getLocalDateString()
  });

  const [imageBase64, setImageBase64] = useState(null);
  const smartProofUrl = smartData?.proofUrl || null;
  const smartScores = smartData?.scores || [];
  const smartConfidence = smartData?.confidence || null;
  const [tableNames, setTableNames] = useState([]);
  const [players, setPlayers] = useState([]);
  const [newPlayerInitials, setNewPlayerInitials] = useState('');
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState('');
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const baseUrl = 'https://pinballscoresapp-ayd8babtd4fpadec.eastus2-01.azurewebsites.net/api';

  useEffect(() => {
    const fetchTables = async () => {
      try {
        const res = await axios.get(`${baseUrl}/tables`);
        const names = [...new Set(res.data.map(table => table.Name))];
        setTableNames(names);
      } catch (err) {
        console.error('Error fetching tables:', err);
      }
    };

    const fetchPlayers = async () => {
      try {
        const res = await axios.get(`${baseUrl}/players`);
        setPlayers(res.data);
      } catch (err) {
        console.error('Error fetching players:', err);
      }
    };

    const fetchTournaments = async () => {
      try {
        const res = await axios.get(`${baseUrl}/tournaments?activeOnly=true`);
        setTournaments(res.data);
      } catch (err) {
        console.error('Error fetching tournaments:', err);
      }
    };

    fetchTables();
    fetchPlayers();
    fetchTournaments();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // If user changes the table, clear tournament selection if it doesn't match
    if (name === 'tableName') {
      if (selectedTournament) {
        const currentTournament = tournaments.find(t => String(t.TournamentID) === selectedTournament);
        if (currentTournament && currentTournament.TableName !== value) {
          setSelectedTournament('');
        }
      }
    }
  };

  const handleTournamentChange = (e) => {
    const tournamentId = e.target.value;
    setSelectedTournament(tournamentId);

    if (tournamentId) {
      const tournament = tournaments.find(t => String(t.TournamentID) === tournamentId);
      if (tournament) {
        // Auto-fill table name and type from the tournament
        setFormData(prev => ({
          ...prev,
          tableName: tournament.TableName,
          tableType: tournament.TableType || prev.tableType
        }));
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]; // remove data:image/jpeg;base64,
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  // Check if the entered player name matches any existing player
  const isNewPlayer = formData.playerName.trim().length > 0 && !players.some(
    p => p.Name.toLowerCase() === formData.playerName.trim().toLowerCase() ||
      p.Initials.toLowerCase() === formData.playerName.trim().toLowerCase()
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResponse(null);

    if (isNewPlayer && (!newPlayerInitials || newPlayerInitials.trim().length < 2 || newPlayerInitials.trim().length > 3)) {
      setError('New players must provide 2-3 character initials.');
      return;
    }

    try {
      const payload = {
        playerName: formData.playerName,
        tableName: formData.tableName,
        tableType: formData.tableType,
        score: Number(formData.score.replace(/,/g, '')),
        datePlayed: formData.datePlayed,
        imageBase64: imageBase64
      };

      // If we have a proof URL from Smart Submit and no new image was selected, pass it and automatically verify
      if (!imageBase64 && smartProofUrl) {
        payload.proofUrl = smartProofUrl;
        payload.isVerified = true;
      }

      if (isNewPlayer && newPlayerInitials.trim()) {
        payload.playerInitials = newPlayerInitials.trim().toUpperCase();
      }

      const res = await axios.post(`${baseUrl}/scores`, payload);
      setResponse(res.data);
    } catch (err) {
      setError(err.response?.data || 'Error submitting score');
    }
  };

  // Filter tournaments to match the currently selected table (if any)
  const filteredTournaments = formData.tableName
    ? tournaments.filter(t => t.TableName === formData.tableName)
    : tournaments;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', timeZone: 'UTC'
    });
  };

  return (
    <div className="score-form">
      <h2>Submit a Pinball Score</h2>

      {/* Smart Submit AI Results Banner */}
      {smartData && (
        <div className="smart-results-banner">
          <div className="smart-results-header">
            <span className="smart-results-label">🤖 AI-Analyzed Image</span>
            {smartConfidence && (
              <span className={`confidence-badge confidence-${smartConfidence}`}>
                {smartConfidence === 'high' ? '✅' : smartConfidence === 'medium' ? '⚠️' : '❓'}
                {' '}{smartConfidence} confidence
              </span>
            )}
          </div>

          {smartScores.length > 0 && (
            <div className="score-picker">
              <p className="score-picker-label">{smartScores.length > 1 ? 'Select the correct score:' : 'AI-detected score:'}</p>
              <div className="score-picker-options">
                {smartScores.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`score-pill ${String(s.value) === formData.score ? 'score-pill-active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, score: String(s.value) }))}
                  >
                    {Number(s.value).toLocaleString()}
                    {s.playerInitials && (
                      <span className="score-pill-initials">{s.playerInitials}</span>
                    )}
                    {s.displayText && (
                      <span className="score-pill-display">reads: {s.displayText}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {smartProofUrl && (
            <div className="smart-proof-preview">
              <img src={smartProofUrl} alt="Proof screenshot" className="smart-proof-img" />
            </div>
          )}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <label>
          Player Name or Initials:
          <input
            list="player-names"
            name="playerName"
            value={formData.playerName}
            onChange={handleChange}
            placeholder="Type name or initials..."
            required
          />
          <datalist id="player-names">
            {players.map(p => (
              <React.Fragment key={p.PlayerID}>
                <option value={p.Name}>{p.Name} ({p.Initials})</option>
                {p.Initials && <option value={p.Initials}>{p.Initials} — {p.Name}</option>}
              </React.Fragment>
            ))}
          </datalist>
        </label>
        {isNewPlayer && (
          <label>
            New Player — Enter 3-Character Initials:
            <input
              type="text"
              value={newPlayerInitials}
              onChange={e => setNewPlayerInitials(e.target.value.toUpperCase().slice(0, 3))}
              placeholder="e.g. ABC"
              maxLength={3}
              required
              style={{ textTransform: 'uppercase', letterSpacing: '4px', textAlign: 'center', fontWeight: 'bold' }}
            />
            <span style={{ fontSize: '12px', color: '#888', fontFamily: 'system-ui, sans-serif' }}>
              This player wasn't found — they'll be created automatically.
            </span>
          </label>
        )}
        <label>
          Machine Name:
          <input list="table-names" name="tableName" value={formData.tableName} onChange={handleChange} required />
          <datalist id="table-names">
            {tableNames.map((name, index) => (
              <option key={index} value={name} />
            ))}
          </datalist>
        </label>
        <label>
          Machine Type:
          <select name="tableType" value={formData.tableType} onChange={handleChange} required>
            <option value="">Select Type</option>
            <option value="Pro">Pro</option>
            <option value="Premium">Premium</option>
            <option value="LE">LE</option>
            <option value="FX">FX</option>
            <option value="VPX">VPX</option>
            <option value="Retro">Retro</option>
          </select>
        </label>

        {/* Active Tournament Dropdown */}
        {tournaments.length > 0 && (
          <label>
            Active Tournament (optional):
            <select value={selectedTournament} onChange={handleTournamentChange}>
              <option value="">— None —</option>
              {filteredTournaments.map(t => (
                <option key={t.TournamentID} value={t.TournamentID}>
                  {t.Name} ({formatDate(t.StartDate)} – {formatDate(t.EndDate)})
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          Score:
          <input type="number" name="score" value={formData.score} onChange={handleChange} required />
        </label>
        <label>
          Date Played:
          <input type="date" name="datePlayed" value={formData.datePlayed} onChange={handleChange} required />
        </label>
        <label>
          Proof Image:
          {smartProofUrl && !imageBase64 && (
            <div style={{ color: '#060', fontSize: '14px', marginBottom: '8px', fontWeight: 'bold' }}>
              ✅ Using AI-analyzed image
            </div>
          )}
          <input type="file" accept="image/jpeg,image/png" onChange={handleFileChange} />
          {smartProofUrl && !imageBase64 && (
            <span style={{ fontSize: '12px', color: '#888', display: 'block', marginTop: '4px' }}>
              (Leave "No file chosen" to use the image you just uploaded)
            </span>
          )}
        </label>
        <button className="large-button" type="submit">Submit Score</button>
      </form>

      {response && (
        <div className="response success">
          <p>{response.message}</p>
          <p>Score ID: {response.scoreId}</p>
          {response.proofUrl && (
            <p>
              Proof Image: <a href={response.proofUrl} target="_blank" rel="noopener noreferrer">View</a>
              {response.isVerified ? <span> ✅ Verified</span> : <span> ⏳ Pending Verification</span>}
            </p>
          )}

          {response.matchedScores && response.matchedScores.length > 0 && (
            <p>
              OCR Found Scores:{" "}
              {response.matchedScores.map((s, i) => (
                <span key={i}>{s.toLocaleString()} </span>
              ))}
            </p>
          )}

        </div>
      )}
      {error && (
        <div className="response error">
          <p>Error: {error}</p>
        </div>
      )}
      <button className="large-button" onClick={() => navigate('/')}>⬅️ Back to Home</button>
    </div>
  );
};

export default PostScoreForm;
